import {Drawer, Text} from '@mantine/core';
import classNames from 'classnames';
import Image from 'next/image';
import {useRouter} from 'next/router';
import {MdAccountCircle, MdClear, MdMenu, MdOpenInNew} from 'react-icons/md';
import ENGINEER_LOGO_IMAGE_PATH_DEFAULT from '../../../../../public/next-image/common/engineerLogo/default.svg';
import {routes} from '../../../../config/constants';
import {COLOR_BLACK_1} from '../../../../config/constants/colorCodes';
import {
  COLUMNS,
  ENGINEER,
  ENGINEER_COLUMN,
  ENGINEER_COMPANY,
  ENGINEER_DRILL,
  ENGINEER_RECRUITING_INFO,
} from '../../../../config/constants/routes';
import {isReleasedNewRegisterFlow} from '../../../../config/unReleasePage';
import {QUERY_PARAMS_FOR_REDIRECT_FROM_LOGIN_PAGE} from '../../../../core/entities/login/constants';
import {useAuth} from '../../../../core/usecases/auth/AuthContext';
import {useBooleanState} from '../../../services/useBooleanState';
import {CommonLink} from '../../CommonLink';
import {overlayConfig} from '../../modalConfig';
import {HeaderMenu} from '../Menu';
import {isStudentVerificationRequiredMenu} from './studentVerificationRequiredMenu';

type Props = {
  scoutsCount: number;
  interestsCount: number;
  className?: string;
};

const drawerList: Array<{
  name: string;
  url: string;
}> = [
  {
    name: 'AI ESビルダー',
    url: '/engineer/ai-es-builder',
  },
  {
    name: 'スキルチェック',
    url: '/engineer/skill_check/top',
  },
  {
    name: 'AI選考対策',
    url: '/report_analytics/search',
  },
  {
    name: '募集を探す',
    url: '/recruiting_info',
  },
  {
    name: '企業を探す',
    url: '/company',
  },
  {
    name: 'ES・体験記',
    url: '/reports',
  },
  {
    name: 'コラム',
    url: COLUMNS,
  },
  {
    name: 'コミュニティ',
    url: '/questions',
  },
  {
    name: '相談室',
    url: '/box/answers',
  },
  {
    name: '各種設定・その他',
    url: '/settings',
  },
];

const hamburgerList: Array<{
  name: string;
  url: string;
  openInNewTab: boolean;
}> = [
  {
    name: '企業をさがす',
    url: ENGINEER_COMPANY,
    openInNewTab: false,
  },
  {
    name: '募集をさがす',
    url: ENGINEER_RECRUITING_INFO,
    openInNewTab: false,
  },
  {
    name: 'ES・体験記',
    url: '/engineer/company/reports',
    openInNewTab: false,
  },
  {
    name: 'コラム',
    url: ENGINEER_COLUMN,
    openInNewTab: false,
  },
  {
    name: 'イベント',
    url: `${ENGINEER}#events`,
    openInNewTab: false,
  },
  {
    name: '開発ドリル',
    url: ENGINEER_DRILL,
    openInNewTab: false,
  },
  {
    name: 'スキルチェック',
    url: '/engineer/skill_check/top',
    openInNewTab: false,
  },
  {
    name: 'AI ESビルダー',
    url: '/engineer/ai-es-builder',
    openInNewTab: false,
  },
  {
    name: 'AI選考対策',
    url: '/report_analytics/search',
    openInNewTab: true,
  },
  {
    name: '外資就活トップ',
    url: '/',
    openInNewTab: true,
  },
  {
    name: 'コミュニティ',
    url: '/questions',
    openInNewTab: true,
  },
  {
    name: '相談室',
    url: '/box/answers',
    openInNewTab: true,
  },
  {
    name: '各種設定・その他',
    url: '/settings',
    openInNewTab: true,
  },
];

const topPageHamburgerList: Array<{
  name: string;
  url: string;
  openInNewTab: boolean;
}> = [
  {
    name: '企業をさがす',
    url: '#companies',
    openInNewTab: false,
  },
  {
    name: '募集をさがす',
    url: '#recruiting_info',
    openInNewTab: false,
  },
  {
    name: 'ES・体験記',
    url: '#company_reports',
    openInNewTab: false,
  },
  {
    name: 'コラム',
    url: '#columns',
    openInNewTab: false,
  },
  {
    name: 'イベント',
    url: '#events',
    openInNewTab: false,
  },
  {
    name: '開発ドリル',
    url: ENGINEER_DRILL,
    openInNewTab: false,
  },
  {
    name: 'スキルチェック',
    url: '/engineer/skill_check/top',
    openInNewTab: false,
  },
  {
    name: 'AI ESビルダー',
    url: '/engineer/ai-es-builder',
    openInNewTab: false,
  },
  {
    name: 'AI選考対策',
    url: '/report_analytics/search',
    openInNewTab: true,
  },
  {
    name: '外資就活トップ',
    url: '/',
    openInNewTab: true,
  },
  {
    name: 'コミュニティ',
    url: '/questions',
    openInNewTab: true,
  },
  {
    name: '相談室',
    url: '/box/answers',
    openInNewTab: true,
  },
  {
    name: '各種設定・その他',
    url: '/settings',
    openInNewTab: true,
  },
];

export const EngineerMobileHeader: React.FC<Props> = ({className}) => {
  const {user, signOut, status} = useAuth();
  const [drawerOpened, , drawerClose, drawerToggle] = useBooleanState(false);
  const [hamburgerOpened, , _hamburgerClose, hamburgerToggle] =
    useBooleanState(false);
  const router = useRouter();
  const baseHamburger =
    router.pathname === ENGINEER ? topPageHamburgerList : hamburgerList;
  // 大学認証が完了していないユーザーには一部メニューを表示しない
  const isNotStudentVerified =
    isReleasedNewRegisterFlow() && user?.isStudentVerified === false;
  const hamburger = isNotStudentVerified
    ? baseHamburger.filter(
        (item) => !isStudentVerificationRequiredMenu(item.url),
      )
    : baseHamburger;
  const drawer = isNotStudentVerified
    ? drawerList.filter((item) => !isStudentVerificationRequiredMenu(item.url))
    : drawerList;

  if (status !== 'authenticated') {
    return (
      <div
        className={classNames(
          'bg-white flex p-16 items-center justify-between fixed w-full top-0 z-[100]',
          className,
        )}
      >
        <CommonLink
          href={ENGINEER}
          className="flex items-center pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out"
        >
          <Image
            src={ENGINEER_LOGO_IMAGE_PATH_DEFAULT}
            width="194"
            height="18"
            alt="外資就活ドットコム"
          />
        </CommonLink>

        <CommonLink
          href={`${routes.LOGIN}?${QUERY_PARAMS_FOR_REDIRECT_FROM_LOGIN_PAGE}=${router.asPath}`}
          className="text-sm font-bold text-primary"
        >
          ログイン・新規登録
        </CommonLink>
      </div>
    );
  }

  return (
    <div>
      <div
        className={classNames(
          'bg-white flex py-[14px] px-16 items-center justify-between fixed w-full top-0 z-[100]',
          className,
        )}
      >
        <div className="flex items-center">
          <button
            id="drawer-button"
            type="button"
            aria-label={hamburgerOpened ? 'メニューを閉じる' : 'メニューを開く'}
            onClick={hamburgerToggle}
            className="pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out "
          >
            {hamburgerOpened ? (
              <MdClear size={24} fill={COLOR_BLACK_1} />
            ) : (
              <div className="relative">
                <MdMenu fill={COLOR_BLACK_1} size={24} />
              </div>
            )}
          </button>
        </div>
        <CommonLink
          href={ENGINEER}
          className="flex items-center pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out"
        >
          <Image
            src={ENGINEER_LOGO_IMAGE_PATH_DEFAULT}
            width="194"
            height="18"
            alt="外資就活ドットコム"
          />
        </CommonLink>
        <button
          className="flex items-center"
          type="button"
          onClick={drawerToggle}
          aria-label={drawerOpened ? 'ドロワーを閉じる' : 'ドロワーを開く'}
        >
          <MdAccountCircle fill={COLOR_BLACK_1} size={20} />
        </button>
        <Drawer
          position="left"
          withCloseButton={false}
          opened={drawerOpened}
          onClose={() => {
            drawerClose();
          }}
          padding={0}
          size="70%"
          className="top-52"
          {...overlayConfig}
        >
          <div className="h-full bg-background-2 p-16">
            <div className="overflow-hidden rounded-12 bg-white-1 p-12">
              {user && (
                <div className="flex gap-8 px-12 pb-4 pt-12">
                  <MdAccountCircle size={24} className="text-black-1" />
                  <div className="font-bold text-black-1">
                    {user.familyName}さん
                  </div>
                </div>
              )}
              <HeaderMenu
                onLogout={signOut}
                showMyPage={true}
                className="w-full bg-white-1 text-black-1"
              />
            </div>
            <ul className="mt-12 flex flex-col divide-y divide-line">
              {drawer.map((item) => {
                return (
                  <li key={item.name} className="p-12">
                    <CommonLink
                      href={
                        // エンジニア学生にはデフォルト「エンジニア志望向け」にチェックが入っている状態で遷移させる
                        item.url === '/recruiting_info' && user?.isEngineer
                          ? `${item.url}?label_ids=3`
                          : item.url
                      }
                      className="flex items-center font-bold leading text-black-1"
                    >
                      <span className="ml-8">{item.name}</span>
                    </CommonLink>
                  </li>
                );
              })}
            </ul>
          </div>
        </Drawer>
        {hamburgerOpened && (
          <div className="absolute top-48 left-16 bg-white shadow-modal p-12 flex flex-col z-10 rounded-12">
            {hamburger.map((item) => (
              <CommonLink
                key={item.name}
                href={item.url}
                target={item.openInNewTab ? '_blank' : '_self'}
                className="w-[160px] p-8 border-b-[1px] border-line text-base text-left justify-between flex items-center"
              >
                <Text>{item.name}</Text>
                {item.openInNewTab && <MdOpenInNew />}
              </CommonLink>
            ))}
          </div>
        )}
      </div>
      <div className="bg-white-1 flex py-12 px-16 items-center justify-between text-[13px] font-bold text-black-1 border-t-[1px] border-line mt-[52px]">
        <Text className="m-auto">
          エンジニアとして 理想のファーストキャリアを 目指そう
        </Text>
      </div>
    </div>
  );
};
